import Decimal from "decimal.js";
import {
  getMinorUnits,
  DEFAULT_CURRENCY,
  type CurrencyCode,
} from "../schemas/currency";

/**
 * Formats an amount as a currency string driven by the currency's ISO
 * minor-unit. Returns "-" for null/undefined. Payable amounts always render at
 * the currency's minor-unit precision - configured price-display decimals apply
 * to unit prices only (see `formatUnitPrice`).
 * @param amount - decimal string, number, or null
 * @param currencyCode - ISO-4217 currency code (defaults to USD)
 * @returns formatted currency string (e.g. "$5,031.00")
 */
export const formatCurrency = (
  amount: string | number | null,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY,
): string => {
  if (amount === null || amount === undefined) {
    return "-";
  }
  const digits = getMinorUnits(currencyCode);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(toNumber(amount));
};

/**
 * Formats an amount as currency with the ISO code appended (e.g. "€5.00 EUR"),
 * used to disambiguate converted amounts in multi-currency quotes. Always
 * renders at the currency's minor-unit precision.
 * @param amount - decimal string, number, or null
 * @param currencyCode - ISO-4217 currency code
 * @returns formatted currency string with code, or "-" for null
 */
export const formatCurrencyWithCode = (
  amount: string | number | null,
  currencyCode: CurrencyCode,
): string => {
  if (amount === null || amount === undefined) {
    return "-";
  }
  return `${formatCurrency(amount, currencyCode)} ${currencyCode}`;
};

/**
 * Formats a unit price, allowing one extra fraction digit beyond the
 * currency's minor unit (preserves the 2–3 dp unit-price behavior).
 * @param amount - decimal string, number, or null
 * @param currencyCode - ISO-4217 currency code (defaults to USD)
 * @param fractionDigits - overrides the ISO minor-unit fraction digits (e.g. a
 *   quote's configured `quotePriceRounding.decimals`); widened to the value's
 *   own decimal places so real precision is never hidden
 * @returns formatted unit-price string
 */
export const formatUnitPrice = (
  amount: string | number | null,
  currencyCode: CurrencyCode = DEFAULT_CURRENCY,
  fractionDigits?: number,
): string => {
  if (amount === null || amount === undefined) {
    return "-";
  }
  const minorUnits = getMinorUnits(currencyCode);
  const digits =
    fractionDigits === undefined
      ? undefined
      : resolveDisplayDigits(amount, fractionDigits);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: digits ?? minorUnits,
    maximumFractionDigits: digits ?? minorUnits + 1,
  }).format(toNumber(amount));
};

/**
 * Resolves the fraction digits used to display an amount when a caller passes
 * a display override: the override wins, but widens to the value's own decimal
 * places so real precision is never hidden (e.g. "100.99" with an override of
 * 0 still shows both cents digits). Capped at 20 for Intl compatibility.
 * @param amount - decimal string or number being formatted
 * @param fractionDigits - the requested display fraction digits
 * @returns the fraction digits to format with
 */
const resolveDisplayDigits = (
  amount: string | number,
  fractionDigits: number,
): number =>
  Math.min(Math.max(fractionDigits, new Decimal(amount).decimalPlaces()), 20);

/**
 * Converts a decimal string or number to a number for Intl formatting.
 * @param amount - decimal string or number
 * @returns numeric value
 */
const toNumber = (amount: string | number): number =>
  typeof amount === "string" ? new Decimal(amount).toNumber() : amount;
